import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Box, Alert } from '@mui/material';
import { Icon } from '@iconify/react';
interface RTGSActionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (notes?: string) => void;
  transaction: {
    id: string;
    mtoName: string;
    amountFCY: string;
    currencyFCY: string;
  };
  action: 'send' | 'reverse' | 'settle';
}
const RTGSActionDialog: React.FC<RTGSActionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  transaction,
  action
}) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isCommentRequired = action === 'reverse' || action === 'settle';
  const handleConfirm = async () => {
    if (isCommentRequired && !notes.trim()) {
      setError('Comment is required when marking transaction as success or failed');
      return;
    }
    setLoading(true);
    setError(null);
    await new Promise(resolve => setTimeout(resolve, 1000));
    onConfirm(notes);
    setLoading(false);
    setNotes('');
  };
  const handleClose = () => {
    setNotes('');
    setError(null);
    onClose();
  };
  const actionConfig = {
    send: {
      title: 'Move to RTGS',
      icon: 'lucide:arrow-right-circle',
      color: '#8B5CF6',
      description: 'This transaction will be sent to the bank for RTGS processing.',
      confirmLabel: 'Send to Bank'
    },
    reverse: {
      title: 'Mark as Reversed (Failed)',
      icon: 'lucide:undo-2',
      color: '#EF4444',
      description: 'Mark this RTGS transaction as reversed (failed). A comment is required.',
      confirmLabel: 'Mark as Reversed'
    },
    settle: {
      title: 'Mark as Manually Settled (Success)',
      icon: 'lucide:check-circle',
      color: '#10B981',
      description: 'Mark this RTGS transaction as manually settled (success). A comment is required.',
      confirmLabel: 'Mark as Settled'
    }
  };
  const config = actionConfig[action];
  return <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-id="element-2183" data-mp="2pvzysfz1qw3o">
      <DialogTitle data-id="element-2184" data-mp="33uptxq2b2lpe">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-2185" data-mp="33v9dtcbink62">
          <Icon icon={config.icon} width={24} height={24} color={config.color} data-id="element-2186" data-mp="2zu5weldtlg2q" />
          <Typography variant="h6" fontWeight={600} data-id="element-2187" data-mp="274yz1pq3qwj2">
            {config.title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent data-id="element-2188" data-mp="2kx5cv55s8rfc">
        <Alert severity={action === 'send' ? 'info' : 'warning'} sx={{
        mb: 3
      }} data-id="element-2189" data-mp="2kxev26kl5uah">
          {config.description}
        </Alert>

        <Box sx={{
        mb: 3
      }} data-id="element-2190" data-mp="1sehys9p7ecnr">
          <Typography variant="subtitle2" gutterBottom data-id="element-2191" data-mp="1oer1ro9330as">
            Transaction Details
          </Typography>
          <Box sx={{
          p: 2,
          backgroundColor: '#F8FAFC',
          borderRadius: 1,
          border: '1px solid #E5E7EB'
        }} data-id="element-2192" data-mp="1ea0xvcu4dm3x">
            <Typography variant="body2" data-id="element-2193" data-mp="14cw87312f8id">
              <strong data-id="element-2194" data-mp="14dxqnskeuxa4">Transaction ID:</strong> {transaction.id}
            </Typography>
            <Typography variant="body2" data-id="element-2195" data-mp="19bpi6lh58hds">
              <strong data-id="element-2196" data-mp="1x11rdlch0pg3">MTO:</strong> {transaction.mtoName}
            </Typography>
            <Typography variant="body2" data-id="element-2197" data-mp="1wyxqlc9vc6k1">
              <strong data-id="element-2198" data-mp="2204zxfrntdst">Amount:</strong> {transaction.currencyFCY}{' '}
              {transaction.amountFCY}
            </Typography>
          </Box>
        </Box>

        <TextField fullWidth multiline rows={4} label={isCommentRequired ? 'Comment (Required)' : 'Notes (Optional)'} placeholder={isCommentRequired ? 'Enter mandatory comment...' : 'Add any additional notes or comments...'} value={notes} onChange={e => {
        setNotes(e.target.value);
        if (error) setError(null);
      }} required={isCommentRequired} error={!!error} helperText={error} data-id="element-2199" data-mp="1n4yogd9soms3" />
      </DialogContent>
      <DialogActions sx={{
      p: 3,
      pt: 0
    }} data-id="element-2200" data-mp="n1it7u9u2mrm">
        <Button onClick={handleClose} disabled={loading} data-id="element-2201" data-mp="i6ljel4qc9do">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading || isCommentRequired && !notes.trim()} sx={{
        backgroundColor: config.color,
        '&:hover': {
          backgroundColor: config.color,
          opacity: 0.9
        }
      }} data-id="element-2202" data-mp="1frre3tvr7l9w">
          {loading ? 'Processing...' : config.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>;
};
export default RTGSActionDialog;